import { ReactElement, useEffect, useRef, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  ControlledTextField,
  LogoIcon,
  ProgressButton,
  GoogleIcon,
  MicrosoftIcon,
  GitHubIcon,
  SignUpInLegalFineprint,
} from '@localstack/ui';
import { Alert, Box, Button, IconButton, InputAdornment, Link, Tooltip, Typography } from '@mui/material';
import {
  formatError,
  formatErrorCode,
  OrganizationsService,
  useApiEffect,
  useRoutes,
  UserService,
  useSnackbar,
  VALIDATION_RULES,
  storeAuthToken,
} from '@localstack/services';
import { useForm } from 'react-hook-form';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { IdentityProvider } from '@localstack/types';
import { ExternalLink, PUBLIC_IDENTITY_PROVIDERS } from '@localstack/constants';

import { AppRoute } from '~/config';
import { useHubspotProvider } from '~/contexts/HubspotContext';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { useSSOStart } from '~/hooks/useSSOStart';
import { useRecaptchaV3 } from '~/hooks/useRecaptchaV3';

import { AuthLayout } from './AuthLayout';
import { useLegalDocument, usePasswordFieldVisibility, usePostAuthRedirect } from './common';
import { OrDivider } from './common/utils';

const LoginType = {
  email: 'email',
  sso: 'sso',
} as const;

type LoginTypeValues = (typeof LoginType)[keyof typeof LoginType];
interface FormData {
  username: string;
  password: string;
}

const IDP_ICONS: Record<string, typeof GoogleIcon | null> = {
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
  github: GitHubIcon,
};

const SSOErrorMessage = ({ error }: { error: string }) => (
  <Typography fontSize={13} fontWeight={500} color="error">
    {error}
  </Typography>
);

export const SignIn = (): ReactElement => {
  const { goto, generateUrl } = useRoutes();
  const { reloadUserInfo } = useAuthProvider();
  const { redirect } = usePostAuthRedirect();
  const { cleanupHubspotUser } = useHubspotProvider();
  const { showSnackbar } = useSnackbar();
  const [ssoError, setSsoError] = useState<Optional<string>>(undefined);
  const [searchParams] = useSearchParams();
  const showSessionExpiredAlert = searchParams.get('sessionExpired') === 'true';
  const { initToken } = useRecaptchaV3();

  const { isIdpLoading, initSSOFlow, selectedIdpName } = useSSOStart({
    onSuccess: (userInfo) => redirect(userInfo),
    onError: (err) => setSsoError(err.message),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting: isSigningInUser },
    reset,
  } = useForm<FormData>({ mode: 'all' });

  const { listIdentityProvidersForEmail, isLoading: isLoadingIdps } = useApiEffect(OrganizationsService, [
    'listIdentityProvidersForEmail',
  ]);
  const { signinUser, error: signInError } = useApiEffect(UserService, ['signinUser'], {
    revalidate: ['getUser'],
    suppressErrors: true,
  });
  const { resendActivation, isLoading: isResendingActivation } = useApiEffect(UserService, ['resendActivation'], {
    revalidate: ['getUser'],
  });

  const legalDocumentToSign = useLegalDocument();

  const [maskPassword, setMaskPassword] = useState<boolean>(false);
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [loginType, setLoginType] = useState<LoginTypeValues>('email');
  const passwordFieldRef = useRef<HTMLInputElement>(null);
  const isEmailFlow = loginType === LoginType.email;
  const isSSOFlow = loginType === LoginType.sso;
  const email = watch('username');
  const usernameFieldErrors = formErrors.username;
  const { setShowPasswordField } = usePasswordFieldVisibility(usernameFieldErrors, passwordFieldRef);

  const handleClickMaskPassword = () => setMaskPassword((show) => !show);

  const handleResendActivationEmail = async () => {
    await resendActivation({ email });

    showSnackbar({
      message:
        'The activation email is being resent if an account with the email ' +
        `address "${email}" exists. Please check your inbox and spam folder.`,
      severity: 'success',
    });
  };

  const onSubmit = async (data: FormData) => {
    if (isSSOFlow) {
      setSsoError(undefined);
      const provider = await listIdentityProvidersForEmail(data.username);
      if (!provider.length) {
        return showSnackbar({
          message: 'No providers found with this email address',
          severity: 'error',
        });
      }
      return setProviders(provider);
    }
    const recaptchaToken = await initToken();
    const result = await signinUser({ ...data, recaptcha_token: recaptchaToken });

    // FIXME: error from signIn gets somehow suppressed and we get result=undefined
    if (result?.token) {
      cleanupHubspotUser();
      storeAuthToken(result.token);

      const freshUserInfo = await reloadUserInfo();
      if (freshUserInfo) redirect(freshUserInfo);
    }
  };

  const handleEmailCancelHandler = () => {
    reset();
  };

  useEffect(() => {
    if (loginType === LoginType.sso) {
      setShowPasswordField(false);
    }
    reset();
    setSsoError(undefined);
  }, [loginType]);

  return (
    <AuthLayout documentTitle="Login">
      <Box textAlign="center">
        <Box mb={4}>
          <LogoIcon />
        </Box>
        <Typography variant="h3" component="h2" fontWeight={600}>
          Sign In to LocalStack
        </Typography>
        <Box mt={1}>
          {isEmailFlow && (
            <Typography fontSize={13} color="text.tertiary" fontWeight={500}>
              New to LocalStack?{' '}
              <Link
                href={ExternalLink.MARKETING_PRICING}
                underline="hover"
                color="text.primary"
                display="inline-flex"
                alignItems="center"
                columnGap={0.5}
                tabIndex={0}
              >
                Explore our plans
              </Link>
            </Typography>
          )}
          {isSSOFlow && (
            <Typography fontSize={13} fontWeight={500}>
              <Link
                underline="hover"
                color="text.primary"
                display="inline-flex"
                alignItems="center"
                columnGap={0.5}
                component="button"
                tabIndex={0}
                onClick={() => {
                  setProviders([]);
                  setLoginType(LoginType.email);
                }}
              >
                Back to login
              </Link>
            </Typography>
          )}
        </Box>
        {showSessionExpiredAlert && (
          <Box mt={2}>
            <Alert
              sx={{ textAlign: 'left' }}
              severity="info"
              variant="outlined"
              onClose={() => {
                searchParams.delete('sessionExpired');
                goto(AppRoute.SIGN_IN, undefined, searchParams.toString());
              }}
            >
              Your session has expired
            </Alert>
          </Box>
        )}
        {signInError && (
          <Box mt={2}>
            <Alert
              sx={{ textAlign: 'left' }}
              severity="error"
              variant="outlined"
              action={
                formatErrorCode(signInError) === 'auth.not_activated' && (
                  <ProgressButton size="small" onClick={handleResendActivationEmail} loading={isResendingActivation}>
                    Resend
                  </ProgressButton>
                )
              }
            >
              {formatError(signInError)}
            </Alert>
          </Box>
        )}
        <form onSubmit={handleSubmit(onSubmit)} id="signin">
          {isEmailFlow && (
            <Box display="flex" flexDirection="column" rowGap={1} mt={3.5}>
              {PUBLIC_IDENTITY_PROVIDERS.map(({ orgId, idpName, label }) => {
                const isSelectedIdpLoading = isIdpLoading && selectedIdpName === idpName;
                const Icon = IDP_ICONS[idpName];
                return (
                  <ProgressButton
                    key={idpName}
                    variant="outlined"
                    color="tertiary"
                    fullWidth
                    size="xlarge"
                    data-sso-button={`${orgId}/${idpName}`}
                    sx={{ textTransform: 'none' }}
                    startIcon={Icon && <Icon sx={{ fontSize: 17, padding: '1px' }} />}
                    disabled={isSelectedIdpLoading}
                    loading={isSelectedIdpLoading}
                    onClick={() => {
                      setSsoError(undefined);
                      initSSOFlow({
                        orgId: orgId,
                        idpName: idpName,
                      });
                    }}
                  >
                    Continue with {label}
                  </ProgressButton>
                );
              })}
              <Button
                type="button"
                variant="outlined"
                color="tertiary"
                fullWidth
                size="xlarge"
                sx={{ textTransform: 'none' }}
                startIcon={<BusinessIcon sx={{ fontSize: 17 }} />}
                onClick={() => setLoginType(LoginType.sso)}
              >
                Continue with SSO
              </Button>
            </Box>
          )}
          {ssoError && loginType === LoginType.email && (
            <Box mt={1}>
              <SSOErrorMessage error={ssoError} />
            </Box>
          )}
          <Box my={2}>{isEmailFlow && <OrDivider />}</Box>
          <Box display="flex" flexDirection="column" rowGap={1}>
            <ControlledTextField
              control={control}
              showErrorsOnlyIfTouched={false}
              name="username"
              fullWidth
              placeholder="Enter your email address"
              type="text"
              variant="outlined"
              rules={{
                ...VALIDATION_RULES.email,
                ...VALIDATION_RULES.required,
              }}
              InputProps={{
                endAdornment: email ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleEmailCancelHandler} size="small" aria-label="Clear email address">
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {loginType !== LoginType.sso && (
              <Box flexDirection="column" rowGap={1} alignItems="center">
                <ControlledTextField
                  control={control}
                  showErrorsOnlyIfTouched={false}
                  name="password"
                  ref={passwordFieldRef}
                  fullWidth
                  placeholder="Enter your password"
                  type={maskPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={`${maskPassword ? 'Hide' : 'Show'} Password`}>
                          <IconButton onClick={handleClickMaskPassword} size="small">
                            {maskPassword ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  rules={VALIDATION_RULES.required}
                />
                <Link
                  underline="hover"
                  fontSize={13}
                  fontWeight={500}
                  color="text.primary"
                  display="inline-flex"
                  alignItems="center"
                  columnGap={0.5}
                  tabIndex={0}
                  component={RouterLink}
                  to={generateUrl(AppRoute.RECOVER, {}, undefined, { keepQueryParams: true })}
                  my={1}
                >
                  <span>Forgot Password?</span>
                </Link>
              </Box>
            )}
            {isSSOFlow ? (
              <ProgressButton
                variant="contained"
                color="primary"
                fullWidth
                size="xlarge"
                type="submit"
                loading={isLoadingIdps}
              >
                Continue
              </ProgressButton>
            ) : (
              <ProgressButton
                variant="contained"
                color="primary"
                fullWidth
                size="xlarge"
                type="submit"
                loading={isSigningInUser}
              >
                Sign in
              </ProgressButton>
            )}
          </Box>
          {providers.length ? (
            <Box display="flex" flexDirection="column" rowGap={1} mt={3.5}>
              {providers.map(({ org_id: orgId, idp_name: idpName }) => {
                const providerName = `${orgId}/${idpName}`;
                const isSelectedIdpLoading = isIdpLoading && selectedIdpName === idpName;
                return (
                  <ProgressButton
                    key={providerName}
                    data-sso-button={providerName}
                    variant="outlined"
                    fullWidth
                    size="xlarge"
                    disabled={isSelectedIdpLoading}
                    loading={isSelectedIdpLoading}
                    onClick={() => initSSOFlow({ orgId, idpName })}
                  >
                    Continue with {idpName}
                  </ProgressButton>
                );
              })}
            </Box>
          ) : null}
          {ssoError && loginType === LoginType.sso && (
            <Box mt={1}>
              <SSOErrorMessage error={ssoError} />
            </Box>
          )}
        </form>
        <Box mt={4} px={2.5}>
          <SignUpInLegalFineprint legalDocument={legalDocumentToSign} />
        </Box>
      </Box>
    </AuthLayout>
  );
};
